/**
 * ProductOrderSidebar.jsx  –  v2
 *
 * Améliorations :
 *  • Multi-variantes : l'acheteur peut choisir plusieurs options (Noir×2 + S×1…)
 *  • Calendrier ergonomique avec grille mensuelle (open/closed day highlighting)
 *  • Bloc vendeur : avatar + note + membre depuis
 *  • Traduction complète via namespace `products` → section `order`
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, IconButton, Drawer, useTheme, useMediaQuery,
  CircularProgress, Alert, Button, Divider, Chip, Card, CardContent,
  TextField, Stepper, Step, StepLabel, Avatar, Rating
} from '@mui/material';
import {
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  QrCode as QrCodeIcon,
  Info as InfoIcon,
  Verified as VerifiedIcon,
  CalendarToday as CalendarIcon,
  ShoppingBag as BagIcon,
  Money as MoneyIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Home as HomeIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay,
  isToday, isPast, startOfDay, addMinutes, isAfter, isBefore
} from 'date-fns';
import { fr, enUS, ar as arLocale } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { formatPrice } from '../../config/currencies';
// Note: le calcul de prix est délégué au backend via POST /api/v1/config/calculate-price
import api from '../../services/api';
import StripePaymentModal from '../payment/StripePaymentModal';
import ProductCashConfirmationModal from '../payment/ProductCashConfirmationModal';
import LoadingOverlay from '../common/LoadingOverlay';
import './ProductOrderSidebar.css';
import '../booking/AvailabilityCalendar.css';
import '../payment/StripePaymentModal.css';

// ─── Constantes ─────────────────────────────────────────────────────────────
const DRAWER_WIDTH      = 440;
const PICKUP_DAYS_AHEAD = 60;

// ─── Labels variantes ────────────────────────────────────────────────────────
const VARIANT_TYPE_LABELS = {
  color:'Couleur',size:'Taille',weight:'Poids',material:'Matière',
  style:'Style',flavor:'Parfum / Goût',scent:'Senteur',format:'Format',
  capacity:'Capacité',length:'Longueur',width:'Largeur',height:'Hauteur',
  diameter:'Diamètre',finish:'Finition',pattern:'Motif',edition:'Édition',
  version:'Version',configuration:'Configuration',connectivity:'Connectivité',
  power:'Puissance',voltage:'Tension',age_range:"Tranche d'âge",gender:'Genre',
  cut:'Coupe',fitting:'Ajustement',pack_size:'Quantité / lot',
  storage:'Stockage',ram:'RAM',processor:'Processeur',os:'Système OS',
  resolution:'Résolution',origin:'Origine',certification:'Certification',
  compatibility:'Compatibilité',language:'Langue',subscription:'Abonnement',
  other:'Autre'
};
const vLabel = (type) => VARIANT_TYPE_LABELS[type] || type;

// ─── generatePickupSlots (exporté pour les tests) ────────────────────────────
export const generatePickupSlots = (
  openingHours = [],
  slotDurationMin = 60,
  unavailabilities = [],
  daysAhead = PICKUP_DAYS_AHEAD,
  preparationTimeMin = 0
) => {
  const slots = [];
  const now   = new Date();
  // Le plus tôt possible = maintenant + délai de préparation (minimum 5 min buffer)
  const cutoff = addMinutes(now, Math.max(5, preparationTimeMin));
  const blockedRanges = unavailabilities.map((u) => ({
    from: new Date(u.available_from),
    to:   new Date(u.available_to)
  }));

  for (let d = 0; d < daysAhead; d++) {
    const day = new Date(now);
    day.setDate(day.getDate() + d);
    day.setHours(0, 0, 0, 0);
    const jsDay  = day.getDay();
    const apiDay = jsDay === 0 ? 7 : jsDay;
    const dayHours = openingHours.filter((h) => h.day === apiDay);
    if (dayHours.length === 0) continue;

    for (const hours of dayHours) {
      const [fromH, fromM] = hours.from.split(':').map(Number);
      const [toH,   toM  ] = hours.to.split(':').map(Number);
      const windowEnd = new Date(day);
      windowEnd.setHours(toH, toM, 0, 0);
      let slotStart = new Date(day);
      slotStart.setHours(fromH, fromM, 0, 0);

      while (true) {
        const slotEnd = addMinutes(slotStart, slotDurationMin);
        if (isAfter(slotEnd, windowEnd)) break;
        if (isAfter(cutoff, slotStart)) {
          slotStart = addMinutes(slotStart, slotDurationMin);
          continue;
        }
        const isBlocked = blockedRanges.some(
          (r) => isBefore(slotStart, r.to) && isAfter(slotEnd, r.from)
        );
        if (!isBlocked) {
          const dateLabel = format(slotStart, 'EEEE d MMMM', { locale: fr });
          slots.push({
            id:        slotStart.toISOString(),
            start:     new Date(slotStart),
            end:       new Date(slotEnd),
            dateLabel: dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1),
            timeLabel: `${format(slotStart,'HH:mm')} – ${format(slotEnd,'HH:mm')}`
          });
        }
        slotStart = addMinutes(slotStart, slotDurationMin);
      }
    }
  }
  return slots;
};

// ─── Mini Calendar (grille mensuelle) ────────────────────────────────────────

const WEEKDAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

const MiniCalendar = ({ openingHours, unavailabilities, onDaySelect, selectedDay, locale, preparationTimeMin = 0 }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const unavDates = useMemo(() => (unavailabilities || []).map((u) => ({
    from: new Date(u.available_from),
    to:   new Date(u.available_to)
  })), [unavailabilities]);

  const isDayAvailable = useCallback((day) => {
    // Vérifier que la journée n'est pas entièrement passée compte tenu du délai de préparation
    const cutoff = addMinutes(new Date(), Math.max(5, preparationTimeMin));
    const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
    if (!isAfter(dayEnd, cutoff)) return false;
    const apiDay = day.getDay() === 0 ? 7 : day.getDay();
    if (!openingHours.some((h) => h.day === apiDay)) return false;
    const dayStart = startOfDay(day);
    return !unavDates.some((r) => isBefore(r.from, dayEnd) && isAfter(r.to, dayStart));
  }, [openingHours, unavDates, preparationTimeMin]);

  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd   = endOfMonth(currentMonth);
    const calStart   = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd     = endOfWeek(monthEnd,   { weekStartsOn: 1 });
    const grid = [];
    let d = calStart;
    while (!isAfter(d, calEnd)) { grid.push(new Date(d)); d = addDays(d, 1); }
    return grid;
  }, [currentMonth]);

  return (
    <div className="availability-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-button" onClick={() => setCurrentMonth((m) => subMonths(m, 1))} aria-label="Mois précédent">‹</button>
        <div className="calendar-title">
          <h3>{format(currentMonth, 'MMMM yyyy', { locale: locale || fr })}</h3>
        </div>
        <button className="calendar-nav-button" onClick={() => setCurrentMonth((m) => addMonths(m, 1))} aria-label="Mois suivant">›</button>
      </div>

      <div className="calendar-weekdays">
        {WEEKDAYS_FR.map((d) => <div key={d} className="calendar-weekday">{d}</div>)}
      </div>

      <div className="calendar-grid">
        {days.map((day) => {
          const isOther    = !isSameMonth(day, currentMonth);
          const isPastDay  = isPast(startOfDay(day)) && !isToday(day);
          const available  = !isOther && !isPastDay && isDayAvailable(day);
          const isSelected = selectedDay && isSameDay(day, selectedDay);

          const classes = [
            'calendar-day',
            isOther      ? 'calendar-day--other-month' : null,
            !isOther && isPastDay                       ? 'calendar-day--past'       : null,
            !isOther && !isPastDay && isSelected        ? 'calendar-day--start-date' : null,
            !isOther && !isPastDay && !isSelected && available  ? 'calendar-day--available' : null,
            !isOther && !isPastDay && !isSelected && !available ? 'calendar-day--closed'    : null,
            isToday(day) ? 'calendar-day--today' : null
          ].filter(Boolean).join(' ');

          return (
            <div
              key={day.toISOString()}
              className={classes}
              onClick={() => available && onDaySelect(day)}
              title={available ? 'Disponible' : (!isOther && !isPastDay) ? 'Fermé' : ''}
            >
              <span className="calendar-day-number">{format(day, 'd')}</span>
            </div>
          );
        })}
      </div>

      <div className="calendar-legend">
        <span className="legend-item"><span className="legend-indicator available" />Disponible</span>
        <span className="legend-item"><span className="legend-indicator" style={{ background:'linear-gradient(45deg,#6200ee,#03dac4)' }} />Sélectionné</span>
        <span className="legend-item"><span className="legend-indicator blocked" />Fermé</span>
      </div>
    </div>
  );
};

// ─── Composant principal ─────────────────────────────────────────────────────

const ProductOrderSidebar = ({ product, open = true, onClose }) => {
  const { t, i18n } = useTranslation('products');
  const { user }    = useAuth();
  const { showNotification } = useNotifications();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const dateLocale = i18n.language?.startsWith('fr') ? fr
    : i18n.language?.startsWith('ar') ? arLocale : enUS;

  // ── Données produit ────────────────────────────────────────────────────────
  const variants         = useMemo(() => product?.variants             || [], [product]);
  const openingHours     = useMemo(() => product?.opening_hours        || [], [product]);
  const slotDuration     = useMemo(() => product?.default_slot_duration_minutes || 60, [product]);
  const unavailabilities = useMemo(() => product?.unavailabilityPeriods || [], [product]);
  const preparationTime  = useMemo(() => product?.preparation_time_minutes || 0, [product]);
  const currency         = product?.currency || 'EUR';
  
  // ── Configuration delivery (Phase 2) ───────────────────────────────────────
  const deliveryEnabled = product?.meta?.delivery_enabled === true;
  const hasExpressDelivery = deliveryEnabled && product?.meta?.delivery_config?.express != null;
  
  const [deliveryType, setDeliveryType] = useState('standard'); // 'standard' | 'express'
  
  const deliveryConfig = useMemo(() => {
    if (!deliveryEnabled) return null;
    const config = product?.meta?.delivery_config;
    // Charger standard ou express selon le choix du client
    return deliveryType === 'express' && config?.express 
      ? config.express 
      : config?.standard || null;
  }, [product?.meta, deliveryEnabled, deliveryType]);
  
  const pricingTiers = useMemo(() => {
    if (!deliveryEnabled || !deliveryConfig?.pricing_tiers) return [];
    return deliveryConfig.pricing_tiers;
  }, [deliveryEnabled, deliveryConfig]);
  
  // Debug logs
  useEffect(() => {
    if (product) {
      console.log('📦 Product meta:', product.meta);
      console.log('🚚 Delivery enabled:', deliveryEnabled);
      console.log('⚙️ Delivery config:', deliveryConfig);
      console.log('📊 Pricing tiers:', pricingTiers);
    }
  }, [product, deliveryEnabled, deliveryConfig, pricingTiers]);
  
  // ── États delivery ─────────────────────────────────────────────────────────
  const [fulfillmentMode, setFulfillmentMode] = useState('pickup'); // 'pickup' | 'delivery'
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    postalCode: '',
    city: '',
    country: ''
  });
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  
  // Formateur local robuste : évite le « N/A » de formatPrice pour les montants à 0
  const fmt = (amount) => {
    if (amount == null || isNaN(amount)) return '—';
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: currency || 'EUR',
        minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(amount);
    } catch {
      return formatPrice(amount, currency);
    }
  };

  // ── Panier multi-variantes ─────────────────────────────────────────────────
  // cart: { [`${type}::${value}`]: { type, value, price, qty, maxQty, sku } }
  const [cart, setCart] = useState({});

  const addToCart = (variantType, opt) => {
    const key = `${variantType}::${opt.value}`;
    setCart((prev) => {
      const cur = prev[key]?.qty || 0;
      // Stock LIVE prioritaire (depuis la base), sinon valeur initiale du prop
      const max = liveVariantMap != null
        ? (liveVariantMap[key] ?? 0)
        : (parseInt(opt.quantity, 10) || 0);
      if (cur >= max) return prev;
      // prix_cents → prix en unité principale (ex: 1500 centimes → 15.00)
      const centimes  = opt.price_cents ?? product?.price_cents ?? 0;
      const unitPrice = Math.max(0, centimes) / 100;
      return { ...prev, [key]: { type: variantType, value: opt.value, price: unitPrice, qty: cur + 1, maxQty: max, sku: opt.sku || null } };
    });
  };

  const removeFromCart = (variantType, opt) => {
    const key = `${variantType}::${opt.value}`;
    setCart((prev) => {
      const cur = prev[key]?.qty || 0;
      if (cur <= 0) return prev;
      if (cur === 1) { const next = { ...prev }; delete next[key]; return next; }
      return { ...prev, [key]: { ...prev[key], qty: cur - 1 } };
    });
  };

  const cartItems   = Object.values(cart);
  const cartIsEmpty = cartItems.length === 0;

  // ── Calendrier & créneaux ──────────────────────────────────────────────────
  const [selectedDay,  setSelectedDay ] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [daySlots, setDaySlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');

  const totalQtyRequested = useMemo(
    () => cartItems.reduce((s, i) => s + (i.qty || 0), 0),
    [cartItems]
  );

  // ── Calcul delivery fee basé sur pricing_tiers ────────────────────────────
  const calculateDeliveryFee = useCallback((totalQuantity, tiers) => {
    if (!tiers || tiers.length === 0) return 0;
    
    // Trier les tiers par min_quantity croissant
    const sortedTiers = [...tiers].sort((a, b) => a.min_quantity - b.min_quantity);
    
    // Trouver le dernier tier applicable (plus grande min_quantity <= totalQuantity)
    let applicableTier = null;
    for (const tier of sortedTiers) {
      if (totalQuantity >= tier.min_quantity) {
        applicableTier = tier;
      } else {
        break; // Les tiers suivants ont des min_quantity encore plus élevés
      }
    }
    
    return applicableTier ? applicableTier.price_cents : 0;
  }, []);

  const deliveryFeeCents = useMemo(() => {
    if (fulfillmentMode !== 'delivery' || !deliveryEnabled) return 0;
    return calculateDeliveryFee(totalQtyRequested, pricingTiers);
  }, [fulfillmentMode, deliveryEnabled, totalQtyRequested, pricingTiers, calculateDeliveryFee]);

  const deliveryFee = deliveryFeeCents / 100;

  useEffect(() => {
    let cancelled = false;
    const loadSlots = async () => {
      if (!selectedDay || !product?.id) {
        setDaySlots([]);
        setSlotsError('');
        return;
      }
      setSlotsLoading(true);
      setSlotsError('');
      try {
        const dateStr = format(selectedDay, 'yyyy-MM-dd');
        // Utilise le moteur de créneaux produit indépendant (slot_client_capacity)
        const res = await api.get(`/products/${product.id}/slots`, {
          params: { from: dateStr, to: dateStr }
        });

        const rawSlots = Array.isArray(res?.data?.data?.slots) ? res.data.data.slots : [];
        const mapped = rawSlots.map((s) => {
          const start = new Date(s.start_at);
          const end = new Date(s.end_at);
          return {
            id: `${s.start_at}__${s.end_at}`,
            start,
            end,
            timeLabel: `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`,
            available: !!s.available,
            remaining_units: s.remaining_capacity
          };
        });

        if (!cancelled) {
          setDaySlots(mapped);
        }
      } catch (e) {
        if (!cancelled) {
          setDaySlots([]);
          setSlotsError(t('order.calendar.loadError', 'Impossible de charger les créneaux.'));
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };

    loadSlots();
    return () => { cancelled = true; };
  }, [selectedDay, product?.listing_id, totalQtyRequested]);

  useEffect(() => { setSelectedSlot(null); }, [selectedDay]);

  // ── Calcul prix via backend (commission + TVA EXTRAITS du total, pas ajoutés dessus) ──
  // IMPORTANT: Le subtotal doit inclure la delivery fee AVANT l'appel backend
  const subtotalProducts = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
  const subtotalWithDelivery = subtotalProducts + deliveryFee;
  const subtotal = subtotalWithDelivery;
  
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  useEffect(() => {
    if (subtotal <= 0) { setPriceBreakdown(null); return; }
    let cancelled = false;
    const fetchPrice = async () => {
      setPriceLoading(true);
      try {
        const res = await api.post('/config/calculate-price', { subtotal, currency, listing_id: product?.listing_id || null });
        if (!cancelled && res?.data?.success) setPriceBreakdown(res.data.data);
      } catch (e) {
        if (!cancelled) setPriceBreakdown(null);
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    };
    fetchPrice();
    return () => { cancelled = true; };
  }, [subtotal, currency]);

  // Valeurs dérivées du résultat backend
  const serviceFee       = priceBreakdown?.commission_amount ?? 0;
  const taxAmount        = priceBreakdown?.tax_amount        ?? 0;
  const total            = priceBreakdown?.client_total_amount ?? subtotal;
  const netPrice         = total - serviceFee - taxAmount - deliveryFee; // Prix net = total - fees - taxes - delivery
  const commissionPct    = priceBreakdown?.commission_rate_percent ?? 8;
  const vatPct           = priceBreakdown?.vat_rate_percent ?? 20;

  // ── Demande spéciale ───────────────────────────────────────────────────────
  const [specialRequests, setSpecialRequests] = useState('');

  // ── Étapes & paiement ─────────────────────────────────────────────────────
  const [step,               setStep              ] = useState('selection');
  const [loading,            setLoading           ] = useState(false);
  const [paymentError,       setPaymentError      ] = useState(null);
  const [paymentMethod,      setPaymentMethod     ] = useState(null); // 'cash' | 'bank_transfer' | 'card'
  const [reservationCreated, setReservationCreated] = useState(null);
  const [stripeData,         setStripeData        ] = useState(null);
  const [stripeProcessing,   setStripeProcessing  ] = useState(false);
  const [showCashModal,      setShowCashModal     ] = useState(false);
  const [cashModalData,      setCashModalData     ] = useState(null);

  // ── Stock live des variantes ───────────────────────────────────────────────
  // liveVariantMap: { [`${type}::${value}`]: qtéRestanteEnBase } — null = pas encore chargé
  const [liveVariantMap, setLiveVariantMap] = useState(null);
  const [stockFetching,  setStockFetching ] = useState(false);

  const canOrder = !cartIsEmpty && (fulfillmentMode === 'delivery' ? isDeliveryAddressValid() : selectedSlot !== null);
  
  // ── Validation adresse delivery ───────────────────────────────────────────
  function isDeliveryAddressValid() {
    if (fulfillmentMode !== 'delivery') return true;
    const { street, postalCode, city, country } = deliveryAddress;
    return !!(street.trim() && postalCode.trim() && city.trim() && country.trim());
  }

  // ── Payload commun ─────────────────────────────────────────────────────────
  const buildPayload = async (method) => {
    if (!product.listing_id) return null;
    const variantSelections = cartItems.map((item) => ({
      type: item.type, value: item.value, price: item.price, quantity: item.qty, sku: item.sku || null
    }));
    
    // Calcul du créneau (manuel pour pickup, recherche de créneau disponible pour delivery)
    let startFormatted, endFormatted;
    if (fulfillmentMode === 'pickup') {
      if (!selectedSlot) return null;
      startFormatted = format(selectedSlot.start, "yyyy-MM-dd'T'HH:mm:ss");
      endFormatted   = format(selectedSlot.end,   "yyyy-MM-dd'T'HH:mm:ss");
    } else {
      // Mode delivery: chercher un créneau disponible après 48h
      try {
        const now = new Date();
        const minDeliveryStart = addMinutes(now, 48 * 60); // 48 heures minimum
        
        // Récupérer la config selon le choix du client (deliveryType = 'standard' ou 'express')
        const config = product?.meta?.delivery_config;
        const selectedConfig = deliveryType === 'express' && config?.express 
          ? config.express 
          : config?.standard;
        
        const deliveryDays = selectedConfig?.days || 3;
        
        // Élargir la fenêtre de recherche : 48h minimum jusqu'à 60 jours dans le futur
        // (au lieu de se limiter à 48h + deliveryDays qui peut être trop restrictif)
        const searchWindowEnd = addMinutes(now, 60 * 24 * 60); // 60 jours
        
        const fromDate = format(minDeliveryStart, 'yyyy-MM-dd');
        const toDate = format(searchWindowEnd, 'yyyy-MM-dd');                
        
        const slotsRes = await api.get(`/products/${product.id}/slots`, {
          params: { from: fromDate, to: toDate }
        });
        
        const allSlots = slotsRes?.data?.data?.slots || [];
        console.log(`📊 ${allSlots.length} créneaux reçus de l'API`);
        
        // Filtrer les créneaux disponibles avec capacité suffisante
        const availableSlots = allSlots
          .filter(s => {
            const isAvailable = s.available && s.remaining_capacity >= totalQtyRequested;
            const slotStart = new Date(s.start_at);
            const isAfterMinDelivery = slotStart >= minDeliveryStart;
            return isAvailable && isAfterMinDelivery;
          })
          .map(s => ({
            start: new Date(s.start_at),
            end: new Date(s.end_at),
            remaining: s.remaining_capacity
          }))
          .sort((a, b) => a.start - b.start); // Trier par date croissante
        
        console.log(`✅ ${availableSlots.length} créneaux disponibles après filtrage`);
        
        if (availableSlots.length === 0) {
          showNotification(
            t('order.noAvailableSlot', 'Aucun créneau disponible pour la livraison dans les 60 prochains jours.'),
            'error'
          );
          return null;
        }
        
        // Prendre le premier créneau disponible (le plus proche)
        const selectedDeliverySlot = availableSlots[0];
        startFormatted = format(selectedDeliverySlot.start, "yyyy-MM-dd'T'HH:mm:ss");
        endFormatted   = format(selectedDeliverySlot.end,   "yyyy-MM-dd'T'HH:mm:ss");
                
      } catch (error) {
        console.error('❌ Erreur lors de la recherche de créneau de livraison:', error);
        showNotification(
          t('order.slotSearchError', 'Impossible de trouver un créneau de livraison disponible.'),
          'error'
        );
        return null;
      }
    }
    
    // Adresse pickup (vendeur pour pickup, client pour delivery)
    const pickupAddr = fulfillmentMode === 'pickup'
      ? (product?.pickup_location?.address || (product?.pickup_location ? `${product.pickup_location.lat}, ${product.pickup_location.lng}` : ''))
      : `${deliveryAddress.street}, ${deliveryAddress.postalCode} ${deliveryAddress.city}, ${deliveryAddress.country}`;
    
    // Notes spéciales
    const specialRequestsText = fulfillmentMode === 'pickup'
      ? specialRequests.trim()
      : deliveryInstructions.trim();
    
    const payload = {
      listing_id:         product.listing_id,
      start_date:         startFormatted,
      end_date:           endFormatted,
      start_at:           startFormatted,
      end_at:             endFormatted,
      pickup_address:     pickupAddr,
      return_address:     pickupAddr,
      total_price:        total,
      deposit_amount:     0,
      quantity_requested: totalQtyRequested,
      special_requests:   specialRequestsText,
      payment_method:     method,
      item_type:          'product',
      variant_selections: variantSelections
    };
    
    // Ajouter extra_fees si delivery (même si fee = 0, pour distinguer pickup/delivery)
    if (fulfillmentMode === 'delivery') {
      const applicableTier = pricingTiers
        .filter(t => totalQtyRequested >= t.min_quantity)
        .sort((a, b) => b.min_quantity - a.min_quantity)[0];
      
      payload.extra_fees = {
        delivery_fee_cents: deliveryFeeCents,
        delivery_pricing_tier_applied: applicableTier || null,
        delivery_type: deliveryType, // 'standard' ou 'express'
        delivery_address: deliveryAddress
      };
      
      
    } else {
      console.log('ℹ️ Mode pickup - extra_fees non ajouté');
    }
    
    console.log('📦 Payload final buildPayload:', payload);
    
    return payload;
  };

  // ── Ouvrir le modal de confirmation cash / virement ───────────────────────
  const handleCashReservation = async (method) => {
    if (!user) { showNotification(t('order.loginRequired','Connectez-vous.'), 'warning'); navigate('/login'); return; }
    if (!product.listing_id) { showNotification(t('order.productUnavailable','Produit non disponible.'), 'error'); return; }
    const payload = await buildPayload(method);
    if (!payload) return;
    setCashModalData({
      reservationData: payload,
      breakdown: {
        rental_amount: subtotal,
        deposit_amount: 0,
        service_fee: serviceFee,
        tax_amount: taxAmount,
        client_total: total,
        total_amount: total,
        currency,
        pricing_display_mode: 'included',
        commission_rate_percent: commissionPct,
        vat_rate_percent: vatPct
      }
    });
    setShowCashModal(true);
  };

  // ── Confirmer commande cash / virement ────────────────────────────────────
  const handleCashConfirmed = async () => {
    if (!cashModalData?.reservationData) return;
    setShowCashModal(false);
    setLoading(true); setPaymentError(null);
    try {
      const res = await api.post('/reservations', cashModalData.reservationData);
      showNotification(t('order.orderSuccess','Commande créée !'), 'success');
      const reservationId = res.data?.data?.reservation?.id || res.data?.reservation?.id
        || res.data?.data?.id || res.data?.id;
      if (reservationId) {
        onClose();
        navigate(`/booking/confirmation/${reservationId}`);
      } else {
        setReservationCreated(res.data?.data?.reservation || res.data);
        setStep('success');
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.map(e => e.msg).join(', ')
        || err.response?.data?.message
        || t('order.orderError','Impossible de créer la commande.');
      setPaymentError(msg); showNotification(msg, 'error');
      setShowCashModal(true);
    } finally { setLoading(false); }
  };

  // ── Créer commande carte → ouvre StripePaymentModal inline ───────────────
  const handleStripeReservation = async () => {
    if (!user) { showNotification(t('order.loginRequired','Connectez-vous.'), 'warning'); navigate('/login'); return; }
    if (!product.listing_id) { showNotification(t('order.productUnavailable','Produit non disponible.'), 'error'); return; }
    const basePayload = await buildPayload('card');
    if (!basePayload) return;
    // StripePaymentModal gère lui-même l'appel à POST /reservations/with-stripe
    // On lui passe reservationData + breakdown (comme ReservationSidebar)
    setStripeData({
      reservationData: {
        ...basePayload,
        payment_method: 'card',
        renterName: user?.name || 'Client Krillou',
        renterEmail: user?.email
      },
      breakdown: {
        rental_amount: subtotal,
        deposit_amount: 0,
        service_fee: serviceFee,
        tax_amount: taxAmount,
        client_total: total,
        total_amount: total,
        currency,
        pricing_display_mode: 'included',
        // Taux réels depuis la BDD (source de vérité unique)
        commission_rate_percent: commissionPct,
        vat_rate_percent: vatPct
      }
    });
    setStep('stripe_payment');
  };

  const handlePaymentSuccess = useCallback((completeData) => {
    setStripeData(null); setStripeProcessing(false);
    showNotification(t('order.paymentSuccess','Paiement réussi !'), 'success');
    const reservationId = completeData?.reservation?.id;
    if (reservationId) { onClose(); navigate(`/booking/confirmation/${reservationId}`); }
    else { setReservationCreated(completeData?.reservation || completeData); setStep('success'); }
  }, [showNotification, t, navigate, onClose]);

  const handlePaymentError = useCallback((err) => {
    setStripeData(null); setStripeProcessing(false); setStep('payment');
    setPaymentError(err?.message || t('order.paymentError','Échec du paiement.'));
    showNotification(t('order.paymentError','Échec du paiement. Réessayez.'), 'error');
  }, [showNotification, t]);

  const handlePaymentCancel = useCallback(() => {
    setStripeData(null); setStep('payment');
  }, []);
  // ── Stock live : fetch depuis base + auto-trim du panier ───────────────────────
  const fetchLiveStock = useCallback(async () => {
    if (!product?.id) return null;
    setStockFetching(true);
    try {
      const res = await api.get(`/products/${product.id}/variant-stock`);
      const rawVariants = res?.data?.data?.variants || [];
      const map = {};
      rawVariants.forEach(v => {
        (v.options || []).forEach(opt => {
          map[`${v.type}::${opt.value}`] = parseInt(opt.quantity, 10) || 0;
        });
      });
      // Produit sans variantes : stock global
      if (rawVariants.length === 0) {
        map['simple::default'] = parseInt(res?.data?.data?.quantity, 10) || 0;
      }
      setLiveVariantMap(map);
      return map;
    } catch {
      return null;
    } finally {
      setStockFetching(false);
    }
  }, [product?.id]);

  // Charger le stock dès l'ouverture du sidebar
  useEffect(() => {
    if (open && product?.id) fetchLiveStock();
  }, [open, product?.id]);

  // Auto-corriger le panier si le stock a baissé entre-temps
  useEffect(() => {
    if (!liveVariantMap) return;
    setCart(prev => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([key, item]) => {
        const liveMax = liveVariantMap[key] ?? item.maxQty;
        if (item.qty > liveMax) {
          changed = true;
          if (liveMax > 0) next[key] = { ...item, qty: liveMax, maxQty: liveMax };
          // liveMax === 0 : option épuisée → retirée silencieusement du panier
        } else {
          next[key] = item;
        }
      });
      return changed ? next : prev;
    });
  }, [liveVariantMap]);
  // ── Bloc vendeur ──────────────────────────────────────────────────────────
  const renderVendor = () => {
    const owner = product?.owner;
    if (!owner) return null;
    const memberSince = owner.createdAt || owner.created_at;
    const avatarSrc   = owner.profilePicture || owner.profile_picture;
    const rating      = product?.owner_stats?.averageRating || product?.ownerReputation?.score || null;
    const reviews     = product?.owner_stats?.totalReviews  || product?.ownerReputation?.totalReviews || 0;
    return (
      <div className="po-vendor-block">
        <Avatar src={avatarSrc} sx={{ width:46, height:46, bgcolor:'primary.main', fontSize:'1.1rem', flexShrink:0 }}>
          {owner.name?.charAt(0) || 'V'}
        </Avatar>
        <div className="po-vendor-info">
          <div className="po-vendor-name">
            {owner.name || t('order.vendor.anonymous','Vendeur')}
            {owner.verified && <VerifiedIcon sx={{ fontSize:13, color:'#1976d2', ml:0.4, verticalAlign:'middle' }} />}
          </div>
          {memberSince && (
            <div className="po-vendor-meta">
              {t('order.vendor.memberSince','Membre depuis')} {format(new Date(memberSince), 'MMM yyyy', { locale: dateLocale })}
            </div>
          )}
          {rating !== null && (
            <div className="po-vendor-rating">
              <Rating value={rating} precision={0.1} readOnly size="small" />
              <Typography variant="caption" color="text.secondary">
                {Number(rating).toFixed(1)} ({reviews} {t('order.vendor.reviews','avis')})
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Variantes multi-cart ───────────────────────────────────────────────────
  const renderVariants = () => {
    if (variants.length === 0) return null;
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1.5, display:'flex', alignItems:'center', gap:0.5 }}>
          🎨 {t('order.variants.title','Choisissez vos variantes')}
          {stockFetching && <CircularProgress size={12} sx={{ ml:1 }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display:'block', mb:1.5 }}>
          {t('order.variants.hint',"Ajoutez autant d'options que vous souhaitez.")}
        </Typography>
        {variants.map((variant) => (
          <Box key={variant.type} sx={{ mb:2 }}>
            <Typography variant="caption" sx={{ fontWeight:700, color:'#6200ee', textTransform:'uppercase', letterSpacing:0.5, mb:0.75, display:'block' }}>
              {vLabel(variant.type)}
            </Typography>
            <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
              {variant.options.map((opt) => {
                const key        = `${variant.type}::${opt.value}`;
                // Stock LIVE prioritaire, sinon valeur initiale du prop
                const maxQty     = liveVariantMap != null
                  ? (liveVariantMap[key] ?? 0)
                  : (parseInt(opt.quantity, 10) || 0);
                const inCart     = cart[key]?.qty || 0;
                const outOfStock = maxQty === 0;
                return (
                  <div key={opt.value} className={`variant-option-card${inCart > 0 ? ' variant-option-card--in-cart' : ''}${outOfStock ? ' variant-option-card--sold-out' : ''}`}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Box sx={{ flex:1, minWidth:0 }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.75, flexWrap:'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight:600 }}>{opt.value}</Typography>
                          {opt.price_cents != null && <Chip label={fmt(opt.price_cents / 100)} size="small" color="primary" variant="outlined" sx={{ fontSize:'0.7rem', height:20 }} />}
                          <Chip
                            label={outOfStock ? t('order.variants.soldOut','Épuisé') : `${t('order.variants.stock','Stock')}: ${maxQty}`}
                            size="small"
                            color={outOfStock ? 'error' : maxQty <= 3 ? 'warning' : 'success'}
                            sx={{ fontSize:'0.65rem', height:20 }}
                          />
                        </Box>
                        {opt.sku && <Typography variant="caption" color="text.secondary">SKU: {opt.sku}</Typography>}
                      </Box>
                      {!outOfStock && (
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5, flexShrink:0 }}>
                          <button className="variant-qty-btn" onClick={() => removeFromCart(variant.type, opt)} disabled={inCart <= 0}>−</button>
                          <span className="variant-qty-value">{inCart}</span>
                          <button className="variant-qty-btn" onClick={() => addToCart(variant.type, opt)} disabled={inCart >= maxQty}>+</button>
                        </Box>
                      )}
                    </Box>
                  </div>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  // ── Toggle Fulfillment Mode ────────────────────────────────────────────────
  const renderFulfillmentMode = () => {
    if (!deliveryEnabled) return null; // Si delivery désactivé, on skip
    
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1.5, display:'flex', alignItems:'center', gap:0.5 }}>
          🚚 {t('order.fulfillmentMode.title','Mode de récupération')}
        </Typography>
        <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
          <Card
            variant="outlined"
            onClick={() => setFulfillmentMode('pickup')}
            sx={{
              cursor:'pointer',
              borderColor: fulfillmentMode === 'pickup' ? 'primary.main' : 'divider',
              borderWidth: fulfillmentMode === 'pickup' ? 2 : 1,
              transition: 'border-color 0.15s'
            }}
          >
            <CardContent sx={{ p:'10px !important', display:'flex', alignItems:'center', gap:1.5 }}>
              <HomeIcon sx={{ color: fulfillmentMode === 'pickup' ? 'primary.main' : 'text.secondary' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight:700 }}>
                  {t('order.fulfillmentMode.pickup','Retrait sur place')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('order.fulfillmentMode.pickupDesc','Retrait chez le vendeur')}
                </Typography>
              </Box>
              {fulfillmentMode === 'pickup' && <CheckCircleIcon sx={{ ml:'auto', color:'primary.main', fontSize:20 }} />}
            </CardContent>
          </Card>
          
          <Card
            variant="outlined"
            onClick={() => setFulfillmentMode('delivery')}
            sx={{
              cursor:'pointer',
              borderColor: fulfillmentMode === 'delivery' ? 'primary.main' : 'divider',
              borderWidth: fulfillmentMode === 'delivery' ? 2 : 1,
              transition: 'border-color 0.15s'
            }}
          >
            <CardContent sx={{ p:'10px !important', display:'flex', alignItems:'center', gap:1.5 }}>
              <LocalShippingIcon sx={{ color: fulfillmentMode === 'delivery' ? 'primary.main' : 'text.secondary' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight:700 }}>
                  {t('order.fulfillmentMode.delivery','Livraison à distance')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('order.fulfillmentMode.deliveryDesc','Livraison à votre adresse')}
                </Typography>
              </Box>
              {fulfillmentMode === 'delivery' && <CheckCircleIcon sx={{ ml:'auto', color:'primary.main', fontSize:20 }} />}
            </CardContent>
          </Card>
        </Box>
        
        {/* Toggle Standard/Express si Express disponible */}
        {fulfillmentMode === 'delivery' && hasExpressDelivery && (
          <Box sx={{ mt:2 }}>
            <Typography variant="caption" sx={{ fontWeight:700, display:'block', mb:1 }}>
              ⚡ {t('order.deliveryType.title','Type de livraison')}
            </Typography>
            <Box sx={{ display:'flex', gap:1 }}>
              <Button
                variant={deliveryType === 'standard' ? 'contained' : 'outlined'}
                size="small"
                fullWidth
                onClick={() => setDeliveryType('standard')}
                sx={{ textTransform:'none' }}
              >
                📦 {t('order.deliveryType.standard','Standard')} ({product?.meta?.delivery_config?.standard?.days || 3}j)
              </Button>
              <Button
                variant={deliveryType === 'express' ? 'contained' : 'outlined'}
                size="small"
                fullWidth
                onClick={() => setDeliveryType('express')}
                color="warning"
                sx={{ textTransform:'none' }}
              >
                ⚡ {t('order.deliveryType.express','Express')} ({product?.meta?.delivery_config?.express?.days || 1}j)
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  
  // ── Formulaire Adresse Delivery ────────────────────────────────────────────
  const renderDeliveryAddress = () => {
    if (fulfillmentMode !== 'delivery') return null;
    
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1.5 }}>
          📍 {t('order.deliveryAddress.title','Adresse de livraison')}
        </Typography>
        <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
          <TextField
            label={t('order.deliveryAddress.street','Rue et numéro')}
            placeholder={t('order.deliveryAddress.streetPlaceholder','Ex: 15 rue de la République')}
            value={deliveryAddress.street}
            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street: e.target.value }))}
            size="small"
            fullWidth
            required
            error={deliveryAddress.street.trim() === ''}
          />
          <Box sx={{ display:'flex', gap:1 }}>
            <TextField
              label={t('order.deliveryAddress.postalCode','Code postal')}
              placeholder={t('order.deliveryAddress.postalCodePlaceholder','Ex: 75001')}
              value={deliveryAddress.postalCode}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, postalCode: e.target.value }))}
              size="small"
              sx={{ flex: 1 }}
              required
              error={deliveryAddress.postalCode.trim() === ''}
            />
            <TextField
              label={t('order.deliveryAddress.city','Ville')}
              placeholder={t('order.deliveryAddress.cityPlaceholder','Ex: Paris')}
              value={deliveryAddress.city}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
              size="small"
              sx={{ flex: 2 }}
              required
              error={deliveryAddress.city.trim() === ''}
            />
          </Box>
          <TextField
            label={t('order.deliveryAddress.country','Pays')}
            placeholder={t('order.deliveryAddress.countryPlaceholder','Ex: France')}
            value={deliveryAddress.country}
            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, country: e.target.value }))}
            size="small"
            fullWidth
            required
            error={deliveryAddress.country.trim() === ''}
          />
          <TextField
            label={t('order.deliveryAddress.instructions','Instructions de livraison (optionnel)')}
            placeholder={t('order.deliveryAddress.instructionsPlaceholder','Ex: Laisser devant la porte, sonner au 2ème étage…')}
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            multiline
            rows={2}
            size="small"
            fullWidth
          />
        </Box>
      </Box>
    );
  };

  // ── Quantité simple (sans variantes) ──────────────────────────────────────
  const renderSimpleQty = () => {
    if (variants.length > 0) return null;
    const basePrice = (product?.price_cents ?? 0) / 100;
    const key = 'simple::default';
    const inCart = cart[key]?.qty || 0;
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1.5 }}>{t('order.quantity','Quantité')}</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <button className="variant-qty-btn" onClick={() => setCart((prev) => { if (!prev[key] || prev[key].qty <= 0) return prev; const next = { ...prev, [key]: { ...prev[key], qty: prev[key].qty - 1 } }; if (next[key].qty === 0) { delete next[key]; } return next; })} disabled={inCart <= 0}>−</button>
          <span className="variant-qty-value" style={{ fontSize:'1.1rem' }}>{inCart}</span>
          <button className="variant-qty-btn" onClick={() => {
            const liveMax = liveVariantMap?.['simple::default'] ?? 99;
            setCart((prev) => {
              const cur = prev[key]?.qty || 0;
              if (cur >= liveMax) return prev;
              return { ...prev, [key]: { type:'default', value:'default', price:basePrice, qty: cur + 1, maxQty: liveMax } };
            });
          }}>+</button>
        </Box>
      </Box>
    );
  };

  // ── Calendrier ─────────────────────────────────────────────────────────────
  const renderCalendar = () => {
    // En mode delivery, pas de calendrier manuel
    if (fulfillmentMode === 'delivery') return null;
    
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight:700, mb:1.5, display:'flex', alignItems:'center', gap:0.5 }}>
          <CalendarIcon fontSize="small" color="primary" />
          {t('order.calendar.title','Choisissez un créneau de pickup')}
          {slotDuration && <Chip label={`${slotDuration} min`} size="small" color="secondary" sx={{ ml:'auto', fontSize:'0.65rem', height:20 }} />}
        </Typography>

        {openingHours.length === 0 ? (
          <Alert severity="warning" sx={{ fontSize:'0.8rem' }} icon={<InfoIcon fontSize="small" />}>
            {t('order.calendar.noHours','Le vendeur n\'a pas encore configuré ses horaires de pickup.')}
          </Alert>
        ) : (
          <>
            <MiniCalendar
              openingHours={openingHours}
              unavailabilities={unavailabilities}
              onDaySelect={setSelectedDay}
              selectedDay={selectedDay}
              locale={dateLocale}
              preparationTimeMin={preparationTime}
            />

            {selectedDay && (
              <div className="po-slots">
                <div className="po-slots__header">
                  <ScheduleIcon style={{ fontSize:14 }} />
                  {format(selectedDay, 'EEEE d MMMM', { locale:dateLocale })} — {t('order.calendar.slots','Créneaux')}
                </div>
                {slotsLoading ? (
                  <div className="po-slots__empty">{t('order.calendar.loadingSlots','Chargement…')}</div>
                ) : slotsError ? (
                  <div className="po-slots__empty">{slotsError}</div>
                ) : daySlots.length === 0 ? (
                  <div className="po-slots__empty">{t('order.calendar.noSlots','Aucun créneau disponible pour ce jour.')}</div>
                ) : (
                  <div className="po-slots__grid">
                    {daySlots.map((slot) => (
                      <div
                        key={slot.id}
                        className={`po-slot${!slot.available ? ' po-slot--disabled' : ''}${selectedSlot?.id === slot.id ? ' po-slot--selected' : ''}`}
                        onClick={() => slot.available && setSelectedSlot(selectedSlot?.id === slot.id ? null : slot)}
                      >
                        {slot.timeLabel}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedSlot && (
              <Alert severity="success" sx={{ mt:1, fontSize:'0.78rem', py:0.5 }} icon={<CheckCircleIcon fontSize="small" />}>
                <strong>{t('order.calendar.pickup','Pickup')} :</strong>{' '}
                {format(selectedSlot.start, 'EEE d MMM · HH:mm', { locale:dateLocale })} → {format(selectedSlot.end, 'HH:mm')}
              </Alert>
            )}
          </>
        )}
      </Box>
    );
  };

  // ── Résumé panier ──────────────────────────────────────────────────────────
  const renderCart = () => {
    if (cartIsEmpty) return (
      <Alert severity="info" sx={{ fontSize:'0.8rem' }} icon={<BagIcon fontSize="small" />}>
        {t('order.cart.empty','Votre panier est vide. Sélectionnez au moins une variante.')}
      </Alert>
    );
    return (
      <Card variant="outlined" sx={{ backgroundColor:'grey.50' }}>
        <CardContent sx={{ p:'10px !important' }}>
          <Typography variant="caption" sx={{ fontWeight:700, display:'flex', alignItems:'center', gap:0.5, mb:0.75 }}>
            <BagIcon sx={{ fontSize:14 }} /> {t('order.cart.title','Votre panier')}
          </Typography>
          {cartItems.map((item) => (
            <div key={`${item.type}::${item.value}`} className="cart-line">
              <span className="cart-line__label">{item.type !== 'default' ? `${vLabel(item.type)}: ` : ''}{item.value} ×{item.qty}</span>
              <span className="cart-line__price">{fmt(item.price * item.qty)}</span>
            </div>
          ))}
          
          <Divider sx={{ my:0.75 }} />
          <div className="cart-line">
            <span className="cart-line__label">{t('order.cart.netPrice','Prix net produits')}</span>
            <span className="cart-line__price">{fmt(subtotalProducts)}</span>
          </div>
          
          {/* Ligne livraison / pickup */}
          {fulfillmentMode === 'delivery' && (
            <div className="cart-line" style={{ color:'#6200ee', fontSize:'0.85rem' }}>
              <span className="cart-line__label">
                {deliveryType === 'express' ? '⚡ ' : '🚚 '}
                {deliveryType === 'express' 
                  ? t('order.deliveryFee.express','Livraison express')
                  : t('order.deliveryFee.standard','Livraison standard')}
              </span>
              <span className="cart-line__price">
                {deliveryFee > 0 ? fmt(deliveryFee) : t('order.deliveryFee.free','GRATUITE')}
              </span>
            </div>
          )}
          
          {fulfillmentMode === 'pickup' && (
            <div className="cart-line" style={{ color:'#666', fontSize:'0.8rem' }}>
              <span className="cart-line__label">{t('order.pickup.free','Retrait sur place')}</span>
              <span className="cart-line__price">{t('order.deliveryFee.free','Gratuit')}</span>
            </div>
          )}
          
          <Divider sx={{ my:0.75 }} />
          <div className="cart-line">
            <span className="cart-line__label">{t('order.cart.subtotal','Sous-total')}</span>
            <span className="cart-line__price">{fmt(subtotalWithDelivery)}</span>
          </div>
          
          <div className="cart-line" style={{ color:'#666', fontSize:'0.8rem' }}>
            <span className="cart-line__label">{t('order.cart.serviceFee','Frais de service')} ({commissionPct} %) <em style={{fontSize:'0.72rem'}}></em></span>
            <span className="cart-line__price">{priceLoading ? '…' : fmt(serviceFee)}</span>
          </div>
          {taxAmount > 0 && (
            <div className="cart-line" style={{ color:'#666', fontSize:'0.8rem' }}>
              <span className="cart-line__label">TVA ({vatPct} %) <em style={{fontSize:'0.72rem'}}></em></span>
              <span className="cart-line__price">{priceLoading ? '…' : fmt(taxAmount)}</span>
            </div>
          )}
          <Divider sx={{ my:0.75 }} />
          <div className="cart-line">
            <span className="cart-line__total-label">{t('order.cart.total','Total')}</span>
            <span className="cart-line__total-price">{priceLoading ? '…' : fmt(total)}</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── Étape paiement ─────────────────────────────────────────────────────────
  const renderPaymentStep = () => {
    const METHODS = [
      { id:'cash',         label: t('order.payment.cash','Espèces'),       desc: t('order.payment.cashDesc','Remise en main propre lors du pickup.'), icon: <MoneyIcon />, disabled: false   },
      { id:'bank_transfer',label: t('order.payment.transfer','Virement'),  desc: t('order.payment.transferDesc','Virement bancaire avant le pickup.'), icon: <BankIcon />, disabled: true, badge: t('order.payment.comingSoon','Bientôt disponible')    },
      { id:'card',         label: t('order.payment.card','Carte bancaire'), desc: t('order.payment.cardDesc','Paiement sécurisé via Stripe.'),         icon: <CardIcon />, disabled: false    }
    ];
    const confirmCard    = paymentMethod === 'card';
    const confirmDirect  = paymentMethod === 'cash' || paymentMethod === 'bank_transfer';
    return (
      <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
        {/* Récap */}
        <Card variant="outlined" sx={{ backgroundColor:'grey.50' }}>
          <CardContent sx={{ p:'10px !important' }}>
            <Typography variant="caption" sx={{ fontWeight:700, mb:0.5, display:'block' }}>
              📦 {product?.title}
            </Typography>
            {selectedSlot && (
              <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>
                📅 {format(selectedSlot.start,'EEE d MMM · HH:mm',{locale:dateLocale})} → {format(selectedSlot.end,'HH:mm')}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ fontWeight:700, mt:0.75 }}>
              {t('order.cart.total','Total')} : {fmt(total)}
            </Typography>
          </CardContent>
        </Card>

        {/* Sélection méthode */}
        <Typography variant="subtitle2" sx={{ fontWeight:700 }}>
          {t('order.payment.title','Mode de paiement')}
        </Typography>
        {METHODS.map((m) => (
          <Card
            key={m.id}
            variant="outlined"
            onClick={() => !m.disabled && setPaymentMethod(m.id)}
            sx={{
              cursor: m.disabled ? 'not-allowed' : 'pointer',
              borderColor: paymentMethod===m.id ? 'primary.main' : 'divider',
              borderWidth: paymentMethod===m.id ? 2 : 1,
              transition: 'border-color 0.15s',
              backgroundColor: m.disabled ? 'grey.100' : 'transparent',
              opacity: m.disabled ? 0.6 : 1
            }}
          >
            <CardContent sx={{ p:'10px !important', display:'flex', alignItems:'center', gap:1.5 }}>
              <Box sx={{ color: paymentMethod===m.id ? 'primary.main':'text.secondary' }}>{m.icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight:700 }}>{m.label}</Typography>
                  {m.badge && (
                    <Chip 
                      label={m.badge} 
                      size="small" 
                      color="warning" 
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">{m.desc}</Typography>
              </Box>
              {paymentMethod===m.id && !m.disabled && <CheckCircleIcon sx={{ ml:'auto', color:'primary.main', fontSize:20 }} />}
            </CardContent>
          </Card>
        ))}

        {paymentError && <Alert severity="error" onClose={() => setPaymentError(null)}>{paymentError}</Alert>}

        <Box sx={{ display:'flex', gap:1 }}>
          <Button variant="outlined" fullWidth onClick={() => { setPaymentMethod(null); setStep('selection'); }}>
            {t('order.back','Retour')}
          </Button>
          <Button
            variant="contained" fullWidth
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CartIcon />}
            disabled={!paymentMethod || loading}
            onClick={() => confirmCard ? handleStripeReservation() : handleCashReservation(paymentMethod)}
            sx={{ background:'linear-gradient(45deg,#6200ee 30%,#03dac4 90%)', '&:hover':{background:'linear-gradient(45deg,#03dac4 30%,#6200ee 90%)'} }}
          >
            {loading ? t('order.processing','Traitement…')
              : confirmCard ? t('order.payment.payByCard','Payer par carte')
              : t('order.payment.confirm','Confirmer la commande')}
          </Button>
        </Box>
      </Box>
    );
  };

  // ── Étape sélection ────────────────────────────────────────────────────────
  const renderSelectionStep = () => (
    <Box sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
      {renderVendor()}
      {variants.length > 0 ? renderVariants() : renderSimpleQty()}
      <Divider />
      {renderFulfillmentMode()}
      {renderDeliveryAddress()}
      <Divider />
      {renderCalendar()}
      <Divider />
      {fulfillmentMode === 'pickup' ? (
        <TextField
          label={t('order.specialRequests.label','Demande spéciale (optionnel)')}
          multiline rows={2} fullWidth size="small"
          value={specialRequests}
          onChange={(e) => setSpecialRequests(e.target.value)}
          placeholder={t('order.specialRequests.placeholder','Instructions particulières, emballage cadeau…')}
        />
      ) : null}
      {renderCart()}
      {(cartIsEmpty || (fulfillmentMode === 'pickup' && !selectedSlot) || (fulfillmentMode === 'delivery' && !isDeliveryAddressValid())) && (
        <Alert severity="info" sx={{ fontSize:'0.78rem', py:0.5 }} icon={<InfoIcon fontSize="small" />}>
          {cartIsEmpty && <div>{t('order.validation.addVariant','Ajoutez au moins une variante à votre panier.')}</div>}
          {fulfillmentMode === 'pickup' && !selectedSlot && <div>{t('order.validation.selectSlot','Choisissez un créneau de pickup.')}</div>}
          {fulfillmentMode === 'delivery' && !isDeliveryAddressValid() && <div>{t('order.validation.addressRequired','Veuillez renseigner tous les champs de l\'adresse de livraison.')}</div>}
        </Alert>
      )}
      {liveVariantMap && cartItems.some(item => item.qty > (liveVariantMap[`${item.type}::${item.value}`] ?? Infinity)) && (
        <Alert severity="warning" sx={{ fontSize:'0.78rem', py:0.5 }}>
          {t('order.stockChanged',"Stock insuffisant pour certaines sélections. Votre panier va être ajusté.")}
        </Alert>
      )}
      {paymentError && <Alert severity="error" onClose={() => setPaymentError(null)}>{paymentError}</Alert>}
      <Button
        variant="contained" fullWidth size="large"
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CartIcon />}
        disabled={!canOrder || loading}
        onClick={async () => {
          if (!product.listing_id) { showNotification(t('order.productUnavailable','Produit non disponible.'), 'error'); return; }
          // Re-vérifier le stock en base avant d'aller au paiement
          const freshMap = await fetchLiveStock();
          const mapToCheck = freshMap ?? liveVariantMap;
          if (mapToCheck) {
            const overStock = cartItems.filter(item => {
              const key = `${item.type}::${item.value}`;
              return item.qty > (mapToCheck[key] ?? Infinity);
            });
            if (overStock.length > 0) {
              const names = overStock.map(i => i.value).join(', ');
              showNotification(
                t('order.stockInsufficient', `Stock insuffisant pour : ${names}. Révisez votre panier.`),
                'error'
              );
              return;
            }
          }
          setPaymentMethod(null); setPaymentError(null); setStep('payment');
        }}
        sx={{
          py:1.5, fontSize:'1rem', fontWeight:700,
          background: canOrder ? 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)' : undefined,
          '&:hover':{ background:'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
        }}
      >
        {t('order.orderBtn','Choisir le paiement')}
      </Button>
    </Box>
  );

  // ── Étape succès ───────────────────────────────────────────────────────────
  const renderSuccess = () => (
    <Box sx={{ textAlign:'center', py:4 }}>
      <CheckCircleIcon sx={{ fontSize:72, color:'success.main', mb:2 }} />
      <Typography variant="h5" sx={{ fontWeight:700, mb:1 }}>
        {t('order.success.title','Commande confirmée !')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb:3 }}>
        {t('order.success.ref','Référence')} #{reservationCreated?.id?.slice(0,8)?.toUpperCase()}
      </Typography>
      {selectedSlot && (
        <Card variant="outlined" sx={{ mb:3, textAlign:'left' }}>
          <CardContent sx={{ p:'12px !important' }}>
            <Typography variant="caption" sx={{ fontWeight:700, display:'flex', alignItems:'center', gap:0.5, mb:1 }}>
              <ScheduleIcon fontSize="small" color="primary" />
              {t('order.success.pickupSlot','Créneau de pickup')}
            </Typography>
            <Typography variant="body2"><strong>{t('order.calendar.date','Date')} :</strong> {format(selectedSlot.start,'EEEE d MMMM yyyy',{locale:dateLocale})}</Typography>
            <Typography variant="body2"><strong>{t('order.calendar.time','Heure')} :</strong> {selectedSlot.timeLabel}</Typography>
          </CardContent>
        </Card>
      )}
      <Alert severity="info" icon={<QrCodeIcon />} sx={{ mb:3, textAlign:'left' }}>
        {t('order.success.qrInfo','Le QR code de pickup vous a été envoyé par e-mail et est disponible dans "Mes Commandes".')}
      </Alert>
      <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
        <Button
          variant="contained" fullWidth startIcon={<QrCodeIcon />}
          onClick={() => navigate('/reservations-renter')}
          sx={{ background:'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', '&:hover':{background:'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'} }}
        >
          {t('order.success.viewOrders','Voir mes commandes & QR code')}
        </Button>
        <Button variant="outlined" fullWidth onClick={onClose}>{t('order.success.close','Fermer')}</Button>
      </Box>
    </Box>
  );

  // ── Corps sidebar ──────────────────────────────────────────────────────────
  const sidebarContent = (
    <Box sx={{ width:isMobile?'100vw':DRAWER_WIDTH, height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {/* Header */}
      <Box sx={{ p:2, display:'flex', alignItems:'center', gap:1, background:'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color:'white', flexShrink:0 }}>
        <CartIcon />
        <Box sx={{ flex:1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight:700, lineHeight:1.2 }}>
            {step==='success' ? t('order.success.header','Commande confirmée') : t('order.header','Commander ce produit')}
          </Typography>
          {step==='selection' && product?.title && (
            <Typography variant="caption" sx={{ opacity:0.85, display:'block' }}>{product.title}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color:'white' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Stepper */}
      {step !== 'success' && (
        <Box sx={{ px:2, pt:1.5, pb:0.5, flexShrink:0 }}>
          <Stepper activeStep={step==='selection'?0:step==='payment'?1:2} alternativeLabel>
            <Step><StepLabel>{t('order.steps.selection','Sélection')}</StepLabel></Step>
            <Step><StepLabel>{t('order.steps.payment','Paiement')}</StepLabel></Step>
            <Step><StepLabel>{t('order.steps.confirmation','Confirmation')}</StepLabel></Step>
          </Stepper>
        </Box>
      )}

      <Divider />

      {/* Corps scrollable */}
      <Box sx={{ flex:1, overflowY:'auto', p:2 }}>
        {step==='selection'     && renderSelectionStep()}
        {step==='payment'       && renderPaymentStep()}
        {step==='success'       && renderSuccess()}
        {step==='stripe_payment' && stripeData && (
          <StripePaymentModal
            reservationData={stripeData.reservationData}
            breakdown={stripeData.breakdown}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={handlePaymentCancel}
            onProcessingChange={setStripeProcessing}
          />
        )}
        <ProductCashConfirmationModal
          open={showCashModal}
          onClose={() => { setShowCashModal(false); setCashModalData(null); }}
          onConfirm={handleCashConfirmed}
          reservationData={cashModalData?.reservationData}
          breakdown={cashModalData?.breakdown}
          paymentMethod={paymentMethod}
          loading={loading}
        />
      </Box>

      {/* Overlay de chargement – cash/virement ET soumission carte Stripe */}
      <LoadingOverlay
        isOpen={loading || stripeProcessing}
        title={t('order.loading.title', 'Traitement en cours...')}
        subtitle={t('order.loading.subtitle', 'Votre commande est en cours de traitement. Veuillez patienter...')}
        warning={t('order.loading.warning', 'Ne fermez pas cette page.')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
      />
    </Box>
  );

  return (
    <Drawer
      anchor={isMobile?'bottom':'right'}
      open={open}
      onClose={onClose}
      variant={isMobile?'temporary':'persistent'}
      PaperProps={{ sx:{ width:isMobile?'100%':DRAWER_WIDTH, height:isMobile?'92vh':'100%', borderTopLeftRadius:isMobile?16:0 } }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default ProductOrderSidebar;

